import type {
  QueuePosPayload,
  QueueRosterEntry,
  QueueStartPayload,
  QueueStatePayload,
  RoundEndPayload,
  RoundStandingView,
  SeatPayload,
} from "../networking/ClientNetwork.js";

/**
 * The quickmatch client state machine — pure, DOM-free, driven ONLY by messages
 * the server actually sends (`server/src/rooms/QueueArenaRoom.ts`).
 *
 * The screen it drives is a projection of this model; nothing here touches the
 * network or the document, which is what makes the whole lifecycle testable in
 * node without a browser or a live room.
 *
 * Server phase (`queueState.phase`) → client phase:
 *   lobby → "queued" · countdown → "countdown" · match → "match" ·
 *   intermission → "intermission"
 *
 * The two client-only phases are "connecting" (before the room is joined) and
 * "error" (connect failed / kicked).
 */
export type QuickMatchPhase =
  | "connecting"
  | "queued"
  | "countdown"
  | "match"
  | "intermission"
  | "error";

export interface QuickMatchModel {
  phase: QuickMatchPhase;
  /** Server granted us a seat (`seat {seated:true}`). Waiters are false. */
  seated: boolean;
  /** True once the local `Game` has been booted for a round we are seated in. */
  inGame: boolean;
  /** Set when the server unseats us (winner-stays-on eviction). Cleared on re-seat. */
  evicted: boolean;
  /** Latest `queuePos`. pos = 0 means "not currently queued / unknown". */
  pos: number;
  of: number;
  /**
   * LOCAL epoch ms deadlines. Every server timer is converted from its relative
   * field (`inMs`, `etaMs`, `nextInMs`, `durationS`) against the local clock at
   * receipt. The payloads also carry absolute epochs (`phaseEndsAt`,
   * `nextStartAt`, `config.endsAt`) but those are stamped with the SERVER's
   * clock — using them directly would show wrong timers to any client whose
   * clock is skewed. Relative + local clock is skew-proof.
   */
  phaseEndsAt: number;
  etaAt: number;
  nextStartAt: number;
  roundEndsAt: number;
  /** Config of the round we are currently in. Null between rounds. */
  config: QueueStartPayload["config"] | null;
  roster: QueueRosterEntry[];
  standings: RoundStandingView[];
  errorMessage: string;
}

function emptyModel(): QuickMatchModel {
  return {
    phase: "connecting",
    seated: false,
    inGame: false,
    evicted: false,
    pos: 0,
    of: 0,
    phaseEndsAt: 0,
    etaAt: 0,
    nextStartAt: 0,
    roundEndsAt: 0,
    config: null,
    roster: [],
    standings: [],
    errorMessage: "",
  };
}

export class QuickMatchMachine {
  private m: QuickMatchModel = emptyModel();

  model(): Readonly<QuickMatchModel> {
    return this.m;
  }

  reset(): void {
    this.m = emptyModel();
  }

  /** Room joined + `hello` sent. We are now a waiter until the server says otherwise. */
  connected(): void {
    if (this.m.phase === "connecting") this.m.phase = "queued";
  }

  /** `queueState {phase, inMs, phaseEndsAt}` — broadcast on every server phase flip. */
  queueState(p: QueueStatePayload, now: number = Date.now()): void {
    if (this.m.phase === "error") return;
    this.m.phaseEndsAt = p.inMs > 0 ? now + p.inMs : 0;
    switch (p.phase) {
      case "lobby":
        this.m.phase = "queued";
        break;
      case "countdown":
        this.m.phase = "countdown";
        // The results table has had its intermission; the countdown screen is
        // the match-type reveal, so stop showing last round's standings.
        this.m.standings = [];
        break;
      case "match":
        this.m.phase = "match";
        break;
      case "intermission":
        this.m.phase = "intermission";
        break;
    }
  }

  /** `queuePos {pos, of, etaMs}` — sent to waiters only. */
  queuePos(p: QueuePosPayload, now: number = Date.now()): void {
    this.m.pos = p.pos;
    this.m.of = p.of;
    this.m.etaAt = now + Math.max(0, p.etaMs ?? 0);
  }

  /** `seat {seated, seats?, reason?}` — the seat grant / winner-stays-on eviction. */
  seat(p: SeatPayload): void {
    if (p.seated) {
      this.m.seated = true;
      this.m.evicted = false;
    } else {
      this.m.seated = false;
      this.m.evicted = true;
      // Our PlayerState is gone; whatever the local Game was driving is void.
      this.m.inGame = false;
    }
  }

  /**
   * `start {roster, config}` — round start. Also replayed to mid-round joiners,
   * who are NOT seated: they get the roster so they can see who they're watching.
   *
   * Note the round clock is rebuilt from `config.durationS` and the local clock
   * rather than trusting `config.endsAt` (server epoch). For a mid-round joiner
   * the replayed `start` therefore over-states the time left; the screen only
   * shows that clock to spectators as a soft hint, and the authoritative
   * transition still comes from `roundEnd`.
   */
  start(p: QueueStartPayload, now: number = Date.now()): void {
    this.m.roster = p.roster ?? [];
    this.m.config = p.config ?? null;
    this.m.roundEndsAt = p.config ? now + p.config.durationS * 1000 : 0;
    this.m.phase = "match";
    this.m.standings = [];
  }

  /** `roundEnd {reason, standings, nextInMs, nextStartAt}`. */
  roundEnd(p: RoundEndPayload, now: number = Date.now()): void {
    this.m.standings = p.standings ?? [];
    this.m.nextStartAt = now + Math.max(0, p.nextInMs ?? 0);
    this.m.config = null; // this round is over — do not boot a Game off it
    this.m.roundEndsAt = 0;
    this.m.phase = "intermission";
  }

  error(message: string): void {
    this.m.phase = "error";
    this.m.errorMessage = message;
    this.m.inGame = false;
  }

  /**
   * Deferred spawn, client side: boot the local `Game` only when the server has
   * seated us AND started a round we're in. `config` is nulled at roundEnd, so a
   * stale config can never re-trigger a boot.
   */
  shouldEnterMatch(): boolean {
    return this.m.seated && this.m.phase === "match" && this.m.config !== null && !this.m.inGame;
  }

  markInGame(): void {
    this.m.inGame = true;
  }
}

// ── Projections (pure) ────────────────────────────────────────────────────────

/** The overlay covers everything except a round we are actually playing. */
export function overlayVisible(m: Readonly<QuickMatchModel>): boolean {
  return !(m.seated && m.phase === "match" && m.inGame);
}

/** ms → "M:SS", clamped at zero. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.round((Number.isFinite(ms) ? ms : 0) / 1000));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

/** ETA line for a waiter. The wait is never a mystery — but never a lie either. */
export function formatEta(etaMs: number): string {
  if (!Number.isFinite(etaMs) || etaMs <= 0) return "ANY MOMENT";
  return `~${formatClock(etaMs)}`;
}

/** Milliseconds left on a local-clock deadline. 0 when unset or elapsed. */
export function remainingMs(deadline: number, now: number = Date.now()): number {
  if (!deadline) return 0;
  return Math.max(0, deadline - now);
}

/**
 * The one big number on the screen, and what it means. Everything else is
 * chrome; this is what the player is actually staring at.
 */
export function bigDisplayFor(m: Readonly<QuickMatchModel>, now: number = Date.now()): string {
  switch (m.phase) {
    case "connecting":
      return "···";
    case "error":
      return "!";
    case "countdown":
      // Whole seconds, 6…1 — never "0" while we're still counting.
      return String(Math.max(1, Math.ceil(remainingMs(m.phaseEndsAt, now) / 1000)));
    case "intermission":
      return formatClock(remainingMs(m.nextStartAt, now));
    case "match":
      return m.seated ? "LIVE" : formatClock(remainingMs(m.roundEndsAt, now));
    case "queued":
      return m.seated ? "IN" : (m.pos > 0 ? `#${m.pos}` : "—");
  }
}

/** The line under the big number. */
export function subDisplayFor(m: Readonly<QuickMatchModel>, now: number = Date.now()): string {
  if (m.phase === "connecting" || m.phase === "error") return "";
  if (m.phase === "countdown") return formatMatchType(m.config);
  if (m.phase === "intermission") return "NEXT ROUND";
  if (m.seated) return formatMatchType(m.config);
  return `ETA ${formatEta(remainingMs(m.etaAt, now))}`;
}

export function formatQueuePosition(pos: number, of: number): string {
  if (pos <= 0 || of <= 0) return "FINDING A SLOT…";
  return `POSITION ${pos} OF ${of}`;
}

/** The COUNTDOWN screen's match-type reveal. */
export function formatMatchType(config: QueueStartPayload["config"] | null): string {
  if (!config) return "MOST KILLS WINS";
  const base = `MOST KILLS WINS — ${formatClock(config.durationS * 1000)}`;
  return config.scoreLimit > 0 ? `${base} · FIRST TO ${config.scoreLimit}` : base;
}

export function headerFor(m: Readonly<QuickMatchModel>): string {
  switch (m.phase) {
    case "connecting":   return "CONNECTING";
    case "error":        return "CONNECT FAILED";
    case "countdown":    return m.seated ? "GET READY" : "IN QUEUE";
    case "match":        return m.seated ? "IN MATCH" : "WATCHING — IN QUEUE";
    case "intermission": return "ROUND OVER";
    case "queued":       return m.seated ? "NEXT ROUND" : "IN QUEUE";
  }
}

/** The one line that tells a waiter what is happening to them. */
export function statusLineFor(m: Readonly<QuickMatchModel>): string {
  if (m.phase === "connecting") return "FINDING A GAME…";
  if (m.phase === "error") return m.errorMessage;
  if (m.evicted && !m.seated) return `BACK IN QUEUE — ${formatQueuePosition(m.pos, m.of)}`;
  if (m.seated) {
    if (m.phase === "countdown") return "YOU ARE IN — DROPPING NEXT ROUND";
    if (m.phase === "intermission") return "STAYING ON — NEXT ROUND SOON";
    return "YOU ARE IN";
  }
  if (m.phase === "match") return `${formatQueuePosition(m.pos, m.of)} — WATCHING THE CURRENT ROUND`;
  return formatQueuePosition(m.pos, m.of);
}

export function winnerNameOf(standings: readonly RoundStandingView[]): string {
  return standings.length > 0 ? standings[0].name : "";
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

/**
 * Results table. `standings` arrives already sorted score-descending and with
 * bots excluded (ArenaRoom.buildStandings), so rank is just the index.
 */
export function renderStandings(standings: readonly RoundStandingView[], ownSessionId: string): string {
  if (standings.length === 0) {
    return `<div class="qm-standings-empty">NO STANDINGS — ROUND ENDED EARLY</div>`;
  }
  const rows = standings.map((s, i) => {
    const cls = ["qm-row", s.sessionId === ownSessionId ? "you" : "", i === 0 ? "winner" : ""]
      .filter(Boolean).join(" ");
    return `<div class="${cls}">` +
      `<span class="qm-rank">${i + 1}</span>` +
      `<span class="qm-name">${escapeHtml(s.name)}</span>` +
      `<span class="qm-kills">${s.score}</span>` +
      `<span class="qm-deaths">${s.deaths}</span>` +
      `</div>`;
  }).join("");
  const header = `<div class="qm-row qm-head"><span>#</span><span>PLAYER</span><span>K</span><span>D</span></div>`;
  return header + rows;
}

/** Roster strip for the queue screen — who is currently in the arena. */
export function renderRoster(roster: readonly QueueRosterEntry[]): string {
  if (roster.length === 0) return "";
  return roster.map((r) =>
    `<span class="qm-chip${r.isBot ? " bot" : ""}">${escapeHtml(r.name)}${r.isBot ? " ⛭" : ""}</span>`,
  ).join("");
}
